import React, { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import { formatPHP } from '../utils/format';
import { 
  startOfMonth, 
  endOfMonth, 
  format, 
  subMonths, 
  startOfWeek, 
  endOfWeek, 
  eachWeekOfInterval, 
  isWithinInterval,
  parseISO
} from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiArrowUpRight, FiArrowDownRight, FiPieChart, FiTrendingUp, FiChevronLeft, FiChevronRight } = FiIcons;

const TABLE_EXPENSES = 'expenses_1739880000000';
const TABLE_TOPUPS = 'topups_1739880000000';

const MonthlyDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [data, setData] = useState({
    totalExpenses: 0,
    totalIncome: 0,
    weeklyTrend: [],
    prevMonthComparison: 0,
    topCategories: []
  });

  useEffect(() => {
    fetchMonthlyData();
  }, [currentDate]);

  const fetchMonthlyData = async () => {
    try {
      setLoading(true);
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const prevMonthStart = startOfMonth(subMonths(currentDate, 1));
      const prevMonthEnd = endOfMonth(subMonths(currentDate, 1));

      // Fetch current month expenses & topups
      const [expensesRes, topupsRes, prevExpensesRes] = await Promise.all([
        supabase.from(TABLE_EXPENSES).select('*').gte('purchase_date', format(monthStart, 'yyyy-MM-dd')).lte('purchase_date', format(monthEnd, 'yyyy-MM-dd')),
        supabase.from(TABLE_TOPUPS).select('*').gte('date', format(monthStart, 'yyyy-MM-dd')).lte('date', format(monthEnd, 'yyyy-MM-dd')),
        supabase.from(TABLE_EXPENSES).select('amount').gte('purchase_date', format(prevMonthStart, 'yyyy-MM-dd')).lte('purchase_date', format(prevMonthEnd, 'yyyy-MM-dd'))
      ]);

      const expenses = expensesRes.data || [];
      const topups = topupsRes.data || [];
      const prevExpenses = prevExpensesRes.data || [];

      const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
      const totalIncome = topups.reduce((sum, t) => sum + Number(t.amount), 0);
      const prevTotalExpenses = prevExpenses.reduce((sum, e) => sum + Number(e.amount), 0);

      // Weekly Trend within the month
      const weeks = eachWeekOfInterval({ start: monthStart, end: monthEnd }, { weekStartsOn: 0 });
      const weeklyTrend = weeks.map((week, idx) => {
        const wStart = startOfWeek(week);
        const wEnd = endOfWeek(week);
        const amount = expenses
          .filter(e => {
            const d = parseISO(e.purchase_date);
            return isWithinInterval(d, { start: wStart, end: wEnd });
          })
          .reduce((sum, e) => sum + Number(e.amount), 0);
        
        return { label: `Week ${idx + 1}`, amount };
      });

      // Top Suppliers/Categories for the month
      const supplierMap = {};
      expenses.forEach(e => {
        supplierMap[e.supplier] = (supplierMap[e.supplier] || 0) + Number(e.amount);
      });
      const topCategories = Object.entries(supplierMap)
        .map(([name, amount]) => ({ name, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);

      const diff = prevTotalExpenses === 0 ? 0 : ((totalExpenses - prevTotalExpenses) / prevTotalExpenses) * 100;

      setData({
        totalExpenses,
        totalIncome,
        weeklyTrend,
        prevMonthComparison: diff,
        topCategories
      });
    } catch (err) {
      console.error('Error fetching monthly data:', err);
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (offset) => {
    setCurrentDate(prev => subMonths(prev, -offset));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const netSavings = data.totalIncome - data.totalExpenses;
  const maxWeekly = Math.max(...data.weeklyTrend.map(w => w.amount), 1);

  return (
    <div className="space-y-8 pb-10">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Monthly Overview</h1>
          <p className="text-gray-500 mt-1">Summary for {format(currentDate, 'MMMM yyyy')}</p>
        </div>
        <div className="flex items-center bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
          <button 
            onClick={() => changeMonth(-1)}
            className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <span className="px-4 font-semibold text-gray-700 min-w-[140px] text-center">
            {format(currentDate, 'MMM yyyy')}
          </span>
          <button 
            onClick={() => changeMonth(1)}
            className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>
      </header>

      {/* Monthly Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Income</p>
          <h3 className="text-2xl font-bold text-emerald-600">{formatPHP(data.totalIncome)}</h3>
          <div className="mt-4 flex items-center text-xs text-gray-400">
            <SafeIcon icon={FiArrowUpRight} className="mr-1" />
            <span>Top-ups this month</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Expenses</p>
          <h3 className="text-2xl font-bold text-rose-600">{formatPHP(data.totalExpenses)}</h3>
          <div className={`mt-4 flex items-center text-xs font-medium ${data.prevMonthComparison > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
            <SafeIcon icon={data.prevMonthComparison > 0 ? FiArrowUpRight : FiArrowDownRight} className="mr-1" />
            <span>{Math.abs(data.prevMonthComparison).toFixed(1)}% from last month</span>
          </div>
        </div>

        <div className={`p-6 rounded-2xl shadow-sm border ${netSavings >= 0 ? 'bg-indigo-50 border-indigo-100' : 'bg-rose-50 border-rose-100'}`}>
          <p className={`text-sm font-medium mb-1 ${netSavings >= 0 ? 'text-indigo-800' : 'text-rose-800'}`}>Net Savings</p>
          <h3 className={`text-2xl font-bold ${netSavings >= 0 ? 'text-indigo-900' : 'text-rose-900'}`}>{formatPHP(netSavings)}</h3>
          <div className="mt-4 flex items-center text-xs opacity-60">
            <SafeIcon icon={FiTrendingUp} className="mr-1" />
            <span>Monthly balance diff</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Weekly Trend Chart */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-8 flex items-center">
            <SafeIcon icon={FiTrendingUp} className="mr-2 text-indigo-600" />
            Weekly Spending Trend
          </h2>
          <div className="flex items-end justify-between h-48 px-2">
            {data.weeklyTrend.map((week, idx) => (
              <div key={idx} className="flex flex-col items-center flex-1 group relative">
                <div 
                  className="absolute bottom-full mb-2 bg-gray-900 text-white text-[10px] py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10"
                >
                  {formatPHP(week.amount)}
                </div>
                <div 
                  className="w-full max-w-[32px] bg-indigo-500 rounded-t-lg transition-all duration-500 hover:bg-indigo-600"
                  style={{ height: `${(week.amount / maxWeekly) * 140}px`, minHeight: week.amount > 0 ? '4px' : '0px' }}
                ></div>
                <span className="text-[10px] font-bold text-gray-400 mt-3 truncate w-full text-center">{week.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Monthly Suppliers */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900 flex items-center">
              <SafeIcon icon={FiPieChart} className="mr-2 text-indigo-600" />
              Top Monthly Expenses
            </h2>
          </div>
          <div className="space-y-4">
            {data.topCategories.length === 0 ? (
              <div className="py-12 text-center text-gray-400">No expenses found for this month.</div>
            ) : (
              data.topCategories.map((cat, idx) => (
                <div key={idx} className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-xs">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{cat.name}</p>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div 
                        className="bg-indigo-500 h-full rounded-full"
                        style={{ width: `${(cat.amount / data.totalExpenses) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">{formatPHP(cat.amount)}</p>
                    <p className="text-[10px] text-gray-400">{((cat.amount / data.totalExpenses) * 100).toFixed(0)}%</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyDashboard;